import { localSandboxEnabled, localSession } from '../utils/localSandbox';
import { reportAuthEvent } from '../utils/authTelemetry';
import { useState, useEffect, useCallback } from 'react';
import { signIn, confirmSignIn, signOut, fetchAuthSession } from '@aws-amplify/auth';
import { API_BASE_URL } from '../config/api';
import { saveTokens, clearTokens, getValidToken } from '../utils/tokenManager';
import { STORAGE_KEYS } from '../constants/storage';
import { clearLastRoute } from '../utils/routePersistence';

const STORAGE_KEY = STORAGE_KEYS.AUTH_TOKEN;
const USER_KEY = STORAGE_KEYS.USER;

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Revalidate customer links on every page load; cached contact matches no
  // longer authorize access, including sessions created before phone linking.
  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const storedToken = localStorage.getItem(STORAGE_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      try {
        if (storedToken && storedUser) {
          let parsedUser = JSON.parse(storedUser);
          if (!['admin', 'manager', 'auditor', 'investor'].includes(parsedUser.role)) {
            const validToken = await getValidToken();
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${validToken}` },
            });
            if (!response.ok) throw new Error('Unable to verify account access. Please sign in again.');
            parsedUser = (await response.json()).user;
            if (!parsedUser) throw new Error('Unable to verify account access.');
            if (active) localStorage.setItem(USER_KEY, JSON.stringify(parsedUser));
          }
          if (active) { setToken(localStorage.getItem(STORAGE_KEY)); setUser(parsedUser); }
        }
      } catch {
        if (active) clearTokens();
      } finally {
        if (active) setLoading(false);
      }
    };
    initialize();
    return () => { active = false; };
  }, []);

  /**
   * Finalize login after Cognito auth succeeds — save tokens and fetch user data from DB
   */
  const _handleAuthSuccess = async (session) => {
    const { tokens } = session || await fetchAuthSession();
    const idToken = tokens.idToken.toString();
    const accessToken = tokens.accessToken?.toString();

    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) throw new Error('Failed to fetch user data');

    const { user: newUser } = await response.json();
    if (!newUser) throw new Error('Failed to fetch user data');
    clearTokens();
    saveTokens(idToken, null, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(idToken);
    setUser(newUser);
    reportAuthEvent('sign_in', 'success', idToken);

    return { success: true, user: newUser };
  };

  /**
   * Login with email and password via Cognito (Amplify Auth)
   * Returns mfaRequired: true if Cognito requires a second factor
   */
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      if (localSandboxEnabled) return await _handleAuthSuccess(await localSession('login', { email, password }));
      let result;
      try {
        result = await signIn({ username: email, password });
      } catch (err) {
        if (err.name === 'UserAlreadyAuthenticatedException') {
          await signOut();
          result = await signIn({ username: email, password });
        } else {
          throw err;
        }
      }

      const { isSignedIn, nextStep } = result;

      if (!isSignedIn) {
        reportAuthEvent('sign_in', 'challenge');
        return { success: false, mfaRequired: true, challengeType: nextStep.signInStep };
      }

      return await _handleAuthSuccess();
    } catch (err) {
      reportAuthEvent('sign_in', 'failure');
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Confirm MFA challenge with the code entered by the user
   */
  const confirmMfa = useCallback(async (code) => {
    setLoading(true);
    setError(null);

    try {
      await confirmSignIn({ challengeResponse: code });
      return await _handleAuthSuccess();
    } catch (err) {
      reportAuthEvent('sign_in', 'failure');
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    if (!localSandboxEnabled) await signOut();
    clearTokens();
    localStorage.removeItem(USER_KEY);
    clearLastRoute();
    setToken(null);
    setUser(null);
    setError(null);
    // Redirect to dashboard (login page)
    window.location.href = '/dashboard';
  }, []);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((roles) => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  }, [user]);

  /**
   * Get authorization header for API calls
   * Automatically refreshes token if expired
   */
  const getAuthHeader = useCallback(async () => {
    // Get valid token (will auto-refresh if needed)
    const validToken = await getValidToken();
    return validToken ? { Authorization: `Bearer ${validToken}` } : {};
  }, []);

  /**
   * Make authenticated API call
   * Automatically refreshes token if expired
   */
  const apiCall = useCallback(
    async (url, options = {}) => {
      try {
        // Get auth header (will auto-refresh token if needed)
        const authHeader = await getAuthHeader();
        
        const headers = {
          'Content-Type': 'application/json',
          ...authHeader,
          ...options.headers,
        };

        const response = await fetch(url, {
          ...options,
          headers,
        });

        // If 401, token expired or missing - logout
        if (response.status === 401) {
          logout();
          throw new Error('Session expired. Please login again.');
        }

        // If 403, user lacks permission - don't logout, just show error
        if (response.status === 403) {
          const data = await response.json();
          throw new Error(data.error || 'You do not have permission to perform this action.');
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        return await response.json();
      } catch (err) {
        throw err;
      }
    },
    [getAuthHeader, logout]
  );

  /**
   * Refresh user info from backend
   * Calls /auth/me endpoint to get current user data from database
   */
  const refreshUser = useCallback(async () => {
    const tokenToUse = localStorage.getItem(STORAGE_KEY) || token;
    if (!tokenToUse) {
      return { success: false, error: 'No token available' };
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/me`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenToUse}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          throw new Error('Session expired. Please login again.');
        }
        if (response.status === 403) {
          const errData = await response.json();
          console.warn('refreshUser: Permission denied:', errData);
          return { success: false, error: 'Permission denied' };
        }
        const errData = await response.json();
        console.warn('refreshUser: /me endpoint error:', errData);
        return { success: false };
      }

      const data = await response.json();

      if (data.success && data.user) {
        const updatedUser = {
          ...user,
          ...data.user,
        };
        setUser(updatedUser);
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
        console.warn('refreshUser: /me response not successful:', data);
      }
    } catch (err) {
      console.warn('refreshUser: Error calling /me endpoint:', err.message);
      return { success: false };
    }
  }, [token, logout]);

  // Check both state and localStorage for token
  const storedToken = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  const isAuthenticated = !!(token || storedToken);

  return {
    user,
    token: token || storedToken,
    loading,
    error,
    isAuthenticated,
    login,
    confirmMfa,
    logout,
    hasRole,
    getAuthHeader,
    apiCall,
    refreshUser,
  };
};

/**
 * Protected route component
 * Redirects to login if not authenticated or doesn't have required role
 */
export const ProtectedRoute = ({ children, requiredRoles, authContext }) => {
  const { isAuthenticated, hasRole, loading } = authContext;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <div>Please log in to access this page.</div>;
  }

  if (requiredRoles && !hasRole(requiredRoles)) {
    return <div>You don't have permission to access this page.</div>;
  }

  return children;
};
